import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  // Removed TextField since chat functionality is removed
  IconButton, 
  CircularProgress,
  Chip,
  Button
} from '@mui/material';
import { 
  // Removed Send as SendIcon since chat functionality is removed
  Article as DocumentIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { getUserId } from '../../Utils/authUtils';
// Removed unused AI imports since chat functionality is removed
// import { getAIFeedbackSync } from '../../Utils/aiUtils';
// import { DEEP_DIVES_PROMPT } from '../../Utils/aiPrompts';
import SEOHead from '../SEO/SEOHead';
import SystemDesignGuide from './BlogPosts/SystemDesignGuide';
import ShardingPartitioning from './BlogPosts/CoreConceptsSystemDesign/ShardingPartitioning';
import ConsistentHashing from './BlogPosts/CoreConceptsSystemDesign/ConsistentHashing';
import Replication from './BlogPosts/CoreConceptsSystemDesign/Replication';
import LeaderFollowerArchitecture from './BlogPosts/CoreConceptsSystemDesign/LeaderFollowerArchitecture';
import LoadBalancing from './BlogPosts/CoreConceptsSystemDesign/LoadBalancing';
import CachingStrategies from './BlogPosts/CoreConceptsSystemDesign/CachingStrategies';
import CDN from './BlogPosts/CoreConceptsSystemDesign/CDN';
import AppLevelCache from './BlogPosts/CoreConceptsSystemDesign/AppLevelCache';
import MessageQueues from './BlogPosts/CoreConceptsSystemDesign/MessageQueues';
import PubSubPattern from './BlogPosts/CoreConceptsSystemDesign/PubSubPattern';
import RateLimiting from './BlogPosts/CoreConceptsSystemDesign/RateLimiting';
import CircuitBreakers from './BlogPosts/CoreConceptsSystemDesign/CircuitBreakers';
import CAPTheorem from './BlogPosts/CoreConceptsSystemDesign/CAPTheorem';
import IdempotentOperations from './BlogPosts/CoreConceptsSystemDesign/IdempotentOperations';
import WebSocketsLongPolling from './BlogPosts/CoreConceptsSystemDesign/WebSocketsLongPolling';
import ObjectStorage from './BlogPosts/CoreConceptsSystemDesign/ObjectStorage';
import BloomFilters from './BlogPosts/CoreConceptsSystemDesign/BloomFilters';
import OAuthJWT from './BlogPosts/CoreConceptsSystemDesign/OAuthJWT';
import DeadLetterQueues from './BlogPosts/CoreConceptsSystemDesign/DeadLetterQueues';
import EventualConsistency from './BlogPosts/CoreConceptsSystemDesign/EventualConsistency';
import CQRSEventSourcing from './BlogPosts/CoreConceptsSystemDesign/CQRSEventSourcing';
import MetricsMonitoring from './BlogPosts/CoreConceptsSystemDesign/MetricsMonitoring';
import TimeSeriesDatabases from './BlogPosts/CoreConceptsSystemDesign/TimeSeriesDatabases';

// SOLID Principles imports
import SingleResponsibilityPrinciple from './BlogPosts/SOLIDPrinciples/SingleResponsibilityPrinciple';
import OpenClosedPrinciple from './BlogPosts/SOLIDPrinciples/OpenClosedPrinciple';
import LiskovSubstitutionPrinciple from './BlogPosts/SOLIDPrinciples/LiskovSubstitutionPrinciple';
import InterfaceSegregationPrinciple from './BlogPosts/SOLIDPrinciples/InterfaceSegregationPrinciple';
import DependencyInversionPrinciple from './BlogPosts/SOLIDPrinciples/DependencyInversionPrinciple';

// Design Patterns imports
import SingletonPattern from './BlogPosts/DesignPatterns/Creational/SingletonPattern';
import FactoryMethodPattern from './BlogPosts/DesignPatterns/Creational/FactoryMethodPattern';
import AbstractFactoryPattern from './BlogPosts/DesignPatterns/Creational/AbstractFactoryPattern';
import BuilderPattern from './BlogPosts/DesignPatterns/Creational/BuilderPattern';
import PrototypePattern from './BlogPosts/DesignPatterns/Creational/PrototypePattern';
import AdapterPattern from './BlogPosts/DesignPatterns/Structural/AdapterPattern';
import DecoratorPattern from './BlogPosts/DesignPatterns/Structural/DecoratorPattern';
import FacadePattern from './BlogPosts/DesignPatterns/Structural/FacadePattern';
import CompositePattern from './BlogPosts/DesignPatterns/Structural/CompositePattern';
import ProxyPattern from './BlogPosts/DesignPatterns/Structural/ProxyPattern';
import ObserverPattern from './BlogPosts/DesignPatterns/Behavioral/ObserverPattern';
import StrategyPattern from './BlogPosts/DesignPatterns/Behavioral/StrategyPattern';
import CommandPattern from './BlogPosts/DesignPatterns/Behavioral/CommandPattern';
import TemplateMethodPattern from './BlogPosts/DesignPatterns/Behavioral/TemplateMethodPattern';
import StatePattern from './BlogPosts/DesignPatterns/Behavioral/StatePattern';
import ChainOfResponsibilityPattern from './BlogPosts/DesignPatterns/Behavioral/ChainOfResponsibilityPattern';
import IteratorPattern from './BlogPosts/DesignPatterns/Behavioral/IteratorPattern';
import MediatorPattern from './BlogPosts/DesignPatterns/Behavioral/MediatorPattern';
import MementoPattern from './BlogPosts/DesignPatterns/Behavioral/MementoPattern';
import VisitorPattern from './BlogPosts/DesignPatterns/Behavioral/VisitorPattern';
import InterpreterPattern from './BlogPosts/DesignPatterns/Behavioral/InterpreterPattern';
import BridgePattern from './BlogPosts/DesignPatterns/Structural/BridgePattern';
import FlyweightPattern from './BlogPosts/DesignPatterns/Structural/FlyweightPattern';

// Database Concepts imports
import SagaPattern from './BlogPosts/DatabaseConcepts/SagaPattern';
import MVCCConcurrencyControl from './BlogPosts/DatabaseConcepts/MVCCConcurrencyControl';
import ACIDPropertiesIsolation from './BlogPosts/DatabaseConcepts/ACIDPropertiesIsolation';
import DatabaseKeys from './BlogPosts/DatabaseConcepts/DatabaseKeys';
import SQLvsNoSQL from './BlogPosts/DatabaseConcepts/SQLvsNoSQL';

// Infrastructure DevOps imports
import ContainersVsVMs from './BlogPosts/InfrastructureDevOps/ContainersVsVMs';
import KubernetesGuide from './BlogPosts/InfrastructureDevOps/KubernetesGuide';

import './DeepDives.css';

// Template-based content renderer - replaced old markdown parser

// URL mapping for deep dive posts
const DEEP_DIVE_URL_MAPPING = {
  'system-design-guide': '/deep-dives/a-beginners-guide-to-system-design',
  'sharding-partitioning': '/deep-dives/sharding-partitioning',
  'consistent-hashing': '/deep-dives/consistent-hashing',
  'replication': '/deep-dives/replication',
  'leader-follower-architecture': '/deep-dives/leader-follower-architecture',
  'load-balancing': '/deep-dives/load-balancing',
  'caching-strategies': '/deep-dives/caching-strategies',
  'cdn': '/deep-dives/cdn',
  'app-level-cache': '/deep-dives/app-level-cache',
  'message-queues': '/deep-dives/message-queues',
  'pubsub-pattern': '/deep-dives/pubsub-pattern',
  'rate-limiting': '/deep-dives/rate-limiting',
  'circuit-breakers': '/deep-dives/circuit-breakers',
  'cap-theorem': '/deep-dives/cap-theorem',
  'idempotent-operations': '/deep-dives/idempotent-operations',
  'websockets-long-polling': '/deep-dives/websockets-long-polling',
  'object-storage': '/deep-dives/object-storage',
  'bloom-filters': '/deep-dives/bloom-filters',
  'oauth-jwt': '/deep-dives/oauth-jwt',
  'dead-letter-queues': '/deep-dives/dead-letter-queues',
  'eventual-consistency': '/deep-dives/eventual-consistency',
  'cqrs-event-sourcing': '/deep-dives/cqrs-event-sourcing',
  'metrics-monitoring': '/deep-dives/metrics-monitoring',
  'time-series-databases': '/deep-dives/time-series-databases',
  
  // SOLID Principles
  'single-responsibility-principle': '/deep-dives/single-responsibility-principle',
  'open-closed-principle': '/deep-dives/open-closed-principle',
  'liskov-substitution-principle': '/deep-dives/liskov-substitution-principle',
  'interface-segregation-principle': '/deep-dives/interface-segregation-principle',
  'dependency-inversion-principle': '/deep-dives/dependency-inversion-principle',
  
  // Design Patterns
  'singleton-pattern': '/deep-dives/singleton-pattern',
  'factory-method-pattern': '/deep-dives/factory-method-pattern',
  'abstract-factory-pattern': '/deep-dives/abstract-factory-pattern',
  'builder-pattern': '/deep-dives/builder-pattern',
  'prototype-pattern': '/deep-dives/prototype-pattern',
  'adapter-pattern': '/deep-dives/adapter-pattern',
  'decorator-pattern': '/deep-dives/decorator-pattern',
  'facade-pattern': '/deep-dives/facade-pattern',
  'composite-pattern': '/deep-dives/composite-pattern',
  'proxy-pattern': '/deep-dives/proxy-pattern',
  'observer-pattern': '/deep-dives/observer-pattern',
  'strategy-pattern': '/deep-dives/strategy-pattern',
  'command-pattern': '/deep-dives/command-pattern',
  'template-method-pattern': '/deep-dives/template-method-pattern',
  'state-pattern': '/deep-dives/state-pattern',
  'chain-of-responsibility-pattern': '/deep-dives/chain-of-responsibility-pattern',
  'iterator-pattern': '/deep-dives/iterator-pattern',
  'mediator-pattern': '/deep-dives/mediator-pattern',
  'memento-pattern': '/deep-dives/memento-pattern',
  'visitor-pattern': '/deep-dives/visitor-pattern',
  'interpreter-pattern': '/deep-dives/interpreter-pattern',
  'bridge-pattern': '/deep-dives/bridge-pattern',
  'flyweight-pattern': '/deep-dives/flyweight-pattern',
  
  // Database Concepts
  'saga-pattern': '/deep-dives/saga-pattern',
  'mvcc-concurrency-control': '/deep-dives/mvcc-concurrency-control',
  'acid-properties-isolation': '/deep-dives/acid-properties-isolation',
  'database-keys': '/deep-dives/database-keys',
  'sql-vs-nosql': '/deep-dives/sql-vs-nosql',
  
  // Infrastructure DevOps
  'containers-vs-vms': '/deep-dives/containers-vs-vms',
  'kubernetes-guide': '/deep-dives/kubernetes-guide',
};

// Reverse mapping: slug to document ID
const SLUG_TO_DOC_MAPPING = {
  'a-beginners-guide-to-system-design': 'system-design-guide',
  'sharding-partitioning': 'sharding-partitioning',
  'consistent-hashing': 'consistent-hashing',
  'replication': 'replication',
  'leader-follower-architecture': 'leader-follower-architecture',
  'load-balancing': 'load-balancing',
  'caching-strategies': 'caching-strategies',
  'cdn': 'cdn',
  'app-level-cache': 'app-level-cache',
  'message-queues': 'message-queues',
  'pubsub-pattern': 'pubsub-pattern',
  'rate-limiting': 'rate-limiting',
  'circuit-breakers': 'circuit-breakers',
  'cap-theorem': 'cap-theorem',
  'idempotent-operations': 'idempotent-operations',
  'websockets-long-polling': 'websockets-long-polling',
  'object-storage': 'object-storage',
  'bloom-filters': 'bloom-filters',
  'oauth-jwt': 'oauth-jwt',
  'dead-letter-queues': 'dead-letter-queues',
  'eventual-consistency': 'eventual-consistency',
  'cqrs-event-sourcing': 'cqrs-event-sourcing',
  'metrics-monitoring': 'metrics-monitoring',
  'time-series-databases': 'time-series-databases',
  
  // SOLID Principles
  'single-responsibility-principle': 'single-responsibility-principle',
  'open-closed-principle': 'open-closed-principle',
  'liskov-substitution-principle': 'liskov-substitution-principle',
  'interface-segregation-principle': 'interface-segregation-principle',
  'dependency-inversion-principle': 'dependency-inversion-principle',
  
  // Design Patterns
  'singleton-pattern': 'singleton-pattern',
  'factory-method-pattern': 'factory-method-pattern',
  'abstract-factory-pattern': 'abstract-factory-pattern',
  'builder-pattern': 'builder-pattern',
  'prototype-pattern': 'prototype-pattern',
  'adapter-pattern': 'adapter-pattern',
  'decorator-pattern': 'decorator-pattern',
  'facade-pattern': 'facade-pattern',
  'composite-pattern': 'composite-pattern',
  'proxy-pattern': 'proxy-pattern',
  'observer-pattern': 'observer-pattern',
  'strategy-pattern': 'strategy-pattern',
  'command-pattern': 'command-pattern',
  'template-method-pattern': 'template-method-pattern',
  'state-pattern': 'state-pattern',
  'chain-of-responsibility-pattern': 'chain-of-responsibility-pattern',
  'iterator-pattern': 'iterator-pattern',
  'mediator-pattern': 'mediator-pattern',
  'memento-pattern': 'memento-pattern',
  'visitor-pattern': 'visitor-pattern',
  'interpreter-pattern': 'interpreter-pattern',
  'bridge-pattern': 'bridge-pattern',
  'flyweight-pattern': 'flyweight-pattern',
  
  // Database Concepts
  'saga-pattern': 'saga-pattern',
  'mvcc-concurrency-control': 'mvcc-concurrency-control',
  'acid-properties-isolation': 'acid-properties-isolation',
  'database-keys': 'database-keys',
  'sql-vs-nosql': 'sql-vs-nosql',
  
  // Infrastructure DevOps
  'containers-vs-vms': 'containers-vs-vms',
  'kubernetes-guide': 'kubernetes-guide',
  // Add more mappings for future deep dive posts
  // 'database-design-fundamentals': 'database-design-guide',
  // 'microservices-architecture-guide': 'microservices-guide',
};

// Mock companies data with System Design Guide
const MOCK_COMPANIES = [
  {
    id: 'system-design',
    name: 'System Design Fundamentals',
    docs: [
      {
        id: 'system-design-guide',
        title: 'A Beginner\'s Guide to System Design',
        description: 'Complete guide to mastering system design interviews at FAANG and other tech companies',
        difficulty: 'Beginner to Advanced',
        readTime: '15 min read'
      }
    ]
  },
  {
    id: 'core-concepts',
    name: 'Core Concepts of System Design',
    docs: [
      {
        id: 'sharding-partitioning',
        title: 'Sharding and Partitioning',
        description: 'Scale databases horizontally by breaking data across multiple nodes',
        difficulty: 'Intermediate',
        readTime: '12 min read'
      },
      {
        id: 'consistent-hashing',
        title: 'Consistent Hashing',
        description: 'Distribute keys evenly and handle node changes with minimal data movement',
        difficulty: 'Intermediate',
        readTime: '10 min read'
      },
      {
        id: 'replication',
        title: 'Database Replication',
        description: 'Ensure fault tolerance with data copies across multiple nodes',
        difficulty: 'Intermediate',
        readTime: '14 min read'
      },
      {
        id: 'leader-follower-architecture',
        title: 'Leader-Follower Architecture',
        description: 'Manage consistency in writes and coordinate distributed operations',
        difficulty: 'Advanced',
        readTime: '16 min read'
      },
      {
        id: 'load-balancing',
        title: 'Load Balancing',
        description: 'Smart traffic routing across servers for optimal performance',
        difficulty: 'Intermediate',
        readTime: '13 min read'
      },
      {
        id: 'caching-strategies',
        title: 'Caching Strategies',
        description: 'Master LRU, LFU, TTL and advanced caching patterns for performance',
        difficulty: 'Beginner to Intermediate',
        readTime: '15 min read'
      },
      {
        id: 'cdn',
        title: 'Content Delivery Networks (CDN)',
        description: 'Global edge delivery for fast, reliable content distribution worldwide',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'app-level-cache',
        title: 'Application-Level Caching',
        description: 'Boost performance with Redis and Memcached in-memory caching solutions',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'message-queues',
        title: 'Message Queues',
        description: 'Build scalable systems with Kafka, RabbitMQ, SQS and async messaging',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'pubsub-pattern',
        title: 'Publish-Subscribe Pattern',
        description: 'Enable async notifications and event-driven architecture with decoupled messaging',
        difficulty: 'Intermediate',
        readTime: '20 min read'
      },
      {
        id: 'rate-limiting',
        title: 'Rate Limiting Algorithms',
        description: 'Control system load with Token Bucket, Leaky Bucket, and advanced throttling strategies',
        difficulty: 'Intermediate',
        readTime: '17 min read'
      },
      {
        id: 'circuit-breakers',
        title: 'Circuit Breaker Pattern',
        description: 'Prevent cascading failures with automatic failure detection and recovery mechanisms',
        difficulty: 'Advanced',
        readTime: '19 min read'
      },
      {
        id: 'cap-theorem',
        title: 'CAP Theorem',
        description: 'Understand the fundamental trade-offs between Consistency, Availability, and Partition tolerance',
        difficulty: 'Intermediate to Advanced',
        readTime: '14 min read'
      },
      {
        id: 'idempotent-operations',
        title: 'Idempotent Operations',
        description: 'Enable safe retries and prevent duplicate side effects in distributed systems',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'websockets-long-polling',
        title: 'WebSockets & Long Polling',
        description: 'Build responsive real-time applications with WebSockets and Long Polling techniques',
        difficulty: 'Intermediate',
        readTime: '22 min read'
      },
      {
        id: 'object-storage',
        title: 'Object Storage (S3 Model)',
        description: 'Build scalable, durable storage systems with object storage architecture patterns',
        difficulty: 'Intermediate to Advanced',
        readTime: '20 min read'
      },
      {
        id: 'bloom-filters',
        title: 'Bloom Filters',
        description: 'Achieve fast membership testing with space-efficient probabilistic data structures',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'oauth-jwt',
        title: 'OAuth 2.0 & JWT',
        description: 'Secure user identity and session management with industry-standard protocols',
        difficulty: 'Intermediate to Advanced',
        readTime: '24 min read'
      },
      {
        id: 'dead-letter-queues',
        title: 'Dead Letter Queues',
        description: 'Handle message failures gracefully and ensure reliable system operation',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'eventual-consistency',
        title: 'Eventual Consistency',
        description: 'Balance consistency, availability, and partition tolerance in distributed architectures',
        difficulty: 'Advanced',
        readTime: '20 min read'
      },
      {
        id: 'cqrs-event-sourcing',
        title: 'CQRS & Event Sourcing',
        description: 'Separate read and write models for optimal performance and scalability',
        difficulty: 'Advanced',
        readTime: '26 min read'
      },
      {
        id: 'metrics-monitoring',
        title: 'Metrics & Monitoring',
        description: 'Build comprehensive monitoring with Prometheus, Grafana, and observability best practices',
        difficulty: 'Intermediate to Advanced',
        readTime: '22 min read'
      },
      {
        id: 'time-series-databases',
        title: 'Time-Series Databases',
        description: 'Store and query time-stamped data efficiently with specialized database architectures',
        difficulty: 'Intermediate to Advanced',
        readTime: '21 min read'
      }
    ]
  },
  {
    id: 'solid-principles',
    name: 'SOLID Principles',
    docs: [
      {
        id: 'single-responsibility-principle',
        title: 'Single Responsibility Principle (SRP)',
        description: 'A class should have only one reason to change - master clean, maintainable code design',
        difficulty: 'Beginner',
        readTime: '15 min read'
      },
      {
        id: 'open-closed-principle',
        title: 'Open/Closed Principle (OCP)',
        description: 'Software entities should be open for extension but closed for modification',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'liskov-substitution-principle',
        title: 'Liskov Substitution Principle (LSP)',
        description: 'Objects of a superclass should be replaceable with objects of its subclasses',
        difficulty: 'Advanced',
        readTime: '20 min read'
      },
      {
        id: 'interface-segregation-principle',
        title: 'Interface Segregation Principle (ISP)',
        description: 'No client should be forced to depend on methods it does not use',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'dependency-inversion-principle',
        title: 'Dependency Inversion Principle (DIP)',
        description: 'High-level modules should not depend on low-level modules - both should depend on abstractions',
        difficulty: 'Advanced',
        readTime: '22 min read'
      }
    ]
  },
  {
    id: 'creational-patterns',
    name: 'Creational Design Patterns', 
    docs: [
      {
        id: 'singleton-pattern',
        title: 'Singleton Pattern',
        description: 'Ensure a class has only one instance and provide global access to it',
        difficulty: 'Beginner',
        readTime: '12 min read'
      },
      {
        id: 'factory-method-pattern',
        title: 'Factory Method Pattern',
        description: 'Create objects without specifying their exact classes - master object creation flexibility',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'abstract-factory-pattern',
        title: 'Abstract Factory Pattern',
        description: 'Create families of related objects without specifying their concrete classes',
        difficulty: 'Advanced',
        readTime: '16 min read'
      },
      {
        id: 'builder-pattern',
        title: 'Builder Pattern',
        description: 'Construct complex objects step by step with flexible, readable code',
        difficulty: 'Intermediate',
        readTime: '14 min read'
      },
      {
        id: 'prototype-pattern',
        title: 'Prototype Pattern',
        description: 'Create objects by cloning existing instances - efficient object creation',
        difficulty: 'Intermediate',
        readTime: '13 min read'
      }
    ]
  },
  {
    id: 'structural-patterns',
    name: 'Structural Design Patterns',
    docs: [
      {
        id: 'adapter-pattern',
        title: 'Adapter Pattern',
        description: 'Allow incompatible interfaces to work together - perfect for third-party integration',
        difficulty: 'Beginner',
        readTime: '12 min read'
      },
      {
        id: 'decorator-pattern',
        title: 'Decorator Pattern',
        description: 'Add behavior to objects without altering their structure - flexible enhancement',
        difficulty: 'Intermediate',
        readTime: '15 min read'
      },
      {
        id: 'facade-pattern',
        title: 'Facade Pattern',
        description: 'Provide simplified interface to complex subsystems - hide complexity',
        difficulty: 'Beginner',
        readTime: '13 min read'
      },
      {
        id: 'composite-pattern',
        title: 'Composite Pattern',
        description: 'Compose objects into tree structures - treat individual and composite objects uniformly',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'proxy-pattern',
        title: 'Proxy Pattern',
        description: 'Control access to another object - lazy loading, caching, and access control',
        difficulty: 'Intermediate',
        readTime: '14 min read'
      },
      {
        id: 'bridge-pattern',
        title: 'Bridge Pattern',
        description: 'Separate abstraction from implementation - platform independence',
        difficulty: 'Intermediate',
        readTime: '17 min read'
      },
      {
        id: 'flyweight-pattern',
        title: 'Flyweight Pattern',
        description: 'Minimize memory usage by sharing objects - efficient object management',
        difficulty: 'Advanced',
        readTime: '18 min read'
      }
    ]
  },
  {
    id: 'behavioral-patterns',
    name: 'Behavioral Design Patterns',
    docs: [
      {
        id: 'observer-pattern',
        title: 'Observer Pattern',
        description: 'Define one-to-many dependency between objects - master event-driven architecture',
        difficulty: 'Beginner',
        readTime: '14 min read'
      },
      {
        id: 'strategy-pattern',
        title: 'Strategy Pattern',
        description: 'Define family of algorithms and make them interchangeable - flexible algorithm selection',
        difficulty: 'Beginner',
        readTime: '16 min read'
      },
      {
        id: 'command-pattern',
        title: 'Command Pattern',
        description: 'Encapsulate requests as objects - enable undo/redo and queuing operations',
        difficulty: 'Intermediate',
        readTime: '17 min read'
      },
      {
        id: 'template-method-pattern',
        title: 'Template Method Pattern',
        description: 'Define algorithm skeleton, let subclasses override steps - promote code reuse',
        difficulty: 'Intermediate',
        readTime: '15 min read'
      },
      {
        id: 'state-pattern',
        title: 'State Pattern',
        description: 'Allow objects to alter behavior when internal state changes - finite state machines',
        difficulty: 'Advanced',
        readTime: '18 min read'
      },
      {
        id: 'chain-of-responsibility-pattern',
        title: 'Chain of Responsibility Pattern',
        description: 'Pass requests along a chain of handlers - flexible request processing',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'iterator-pattern',
        title: 'Iterator Pattern',
        description: 'Access collection elements sequentially - uniform traversal interface',
        difficulty: 'Beginner',
        readTime: '14 min read'
      },
      {
        id: 'mediator-pattern',
        title: 'Mediator Pattern',
        description: 'Define how objects interact through a mediator - reduce coupling',
        difficulty: 'Intermediate',
        readTime: '17 min read'
      },
      {
        id: 'memento-pattern',
        title: 'Memento Pattern',
        description: 'Capture and restore object state - implement undo/redo functionality',
        difficulty: 'Intermediate',
        readTime: '15 min read'
      },
      {
        id: 'visitor-pattern',
        title: 'Visitor Pattern',
        description: 'Define operations without changing element classes - extensible operations',
        difficulty: 'Advanced',
        readTime: '19 min read'
      },
      {
        id: 'interpreter-pattern',
        title: 'Interpreter Pattern',
        description: 'Define grammar and interpret language sentences - domain-specific languages',
        difficulty: 'Advanced',
        readTime: '20 min read'
      }
    ]
  },
  {
    id: 'database-concepts',
    name: 'Database Concepts',
    docs: [
      {
        id: 'saga-pattern',
        title: 'Saga Pattern',
        description: 'Manage distributed transactions across microservices - choreography vs orchestration',
        difficulty: 'Advanced',
        readTime: '20 min read'
      },
      {
        id: 'mvcc-concurrency-control',
        title: 'MVCC & Concurrency Control',
        description: 'Multi-Version, Optimistic, and Pessimistic concurrency control strategies',
        difficulty: 'Advanced',
        readTime: '18 min read'
      },
      {
        id: 'acid-properties-isolation',
        title: 'ACID Properties & Isolation',
        description: 'Database transaction guarantees and isolation levels with WAL implementation',
        difficulty: 'Intermediate',
        readTime: '16 min read'
      },
      {
        id: 'database-keys',
        title: 'Database Keys',
        description: 'Primary, Foreign, Composite, and Unique keys - complete guide with examples',
        difficulty: 'Beginner',
        readTime: '14 min read'
      },
      {
        id: 'sql-vs-nosql',
        title: 'SQL vs NoSQL',
        description: 'Complete comparison guide - when to use relational vs document databases',
        difficulty: 'Intermediate',
        readTime: '22 min read'
      }
    ]
  },
  {
    id: 'infrastructure-devops',
    name: 'Infrastructure & DevOps',
    docs: [
      {
        id: 'containers-vs-vms',
        title: 'Containers vs Virtual Machines',
        description: 'Complete virtualization comparison - Docker, hypervisors, performance analysis',
        difficulty: 'Intermediate',
        readTime: '18 min read'
      },
      {
        id: 'kubernetes-guide',
        title: 'Kubernetes Complete Guide',
        description: 'Container orchestration mastery - pods, services, deployments, and best practices',
        difficulty: 'Advanced',
        readTime: '25 min read'
      }
    ]
  }
];

const DeepDives = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set(['system-design', 'core-concepts'])); // Both folders expanded by default
  const [searchTerm, setSearchTerm] = useState('');
  const [topicContent, setTopicContent] = useState(null);
  // Removed chat-related state variables
  // const [chatMessages, setChatMessages] = useState([]);
  // const [chatInput, setChatInput] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  // Removed chat-related loading states
  // const [isSendingMessage, setIsSendingMessage] = useState(false);
  // const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Sidebar state management - only left sidebar now
  const [leftSidebarVisible, setLeftSidebarVisible] = useState(() => {
    const savedVisibility = localStorage.getItem('deepdives-left-sidebar-visible');
    return savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
  });

  // Remove right sidebar state management
  // const [rightSidebarVisible, setRightSidebarVisible] = useState(() => {
  //   const savedVisibility = localStorage.getItem('deepdives-right-sidebar-visible');
  //   return savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
  // });

  // Sidebar width management - only left sidebar now
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem('deepdives-left-sidebar-width');
    return savedWidth ? parseInt(savedWidth) : 280; // Default width
  });

  // Remove right sidebar width management
  // const [rightSidebarWidth, setRightSidebarWidth] = useState(() => {
  //   const savedWidth = localStorage.getItem('deepdives-right-sidebar-width');
  //   return savedWidth ? parseInt(savedWidth) : 350; // Default width
  // });

  // Resize state management - only left sidebar now
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  // Remove right sidebar resize state
  // const [isResizingRight, setIsResizingRight] = useState(false);

  const userId = getUserId();

  // Toggle company folder expansion
  const toggleCompanyExpansion = (companyId) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
    } else {
      newExpanded.add(companyId);
    }
    setExpandedCompanies(newExpanded);
  };

  // Toggle left sidebar visibility
  const toggleLeftSidebar = () => {
    const newVisibility = !leftSidebarVisible;
    setLeftSidebarVisible(newVisibility);
    localStorage.setItem('deepdives-left-sidebar-visible', JSON.stringify(newVisibility));
  };

  // Left sidebar resize handlers
  const handleLeftSidebarMouseDown = (e) => {
    e.preventDefault();
    setIsResizingLeft(true);
  };

  const handleLeftSidebarMouseMove = (e) => {
    if (!isResizingLeft) return;
    
    const newWidth = e.clientX;
    const minWidth = 200;
    const maxWidth = 500;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setLeftSidebarWidth(newWidth);
      localStorage.setItem('deepdives-left-sidebar-width', newWidth.toString());
    }
  };

  const handleLeftSidebarMouseUp = () => {
    setIsResizingLeft(false);
  };

  // Load topic content function - simplified to use only static components
  const loadTopicContent = useCallback((topicId) => {
    setIsLoadingContent(true);
    
    // Direct component mapping - no API call needed
    if (topicId === 'system-design-guide') {
      setTopicContent({ component: 'SystemDesignGuide' });
        } else if (topicId === 'sharding-partitioning') {
          setTopicContent({ component: 'ShardingPartitioning' });
        } else if (topicId === 'consistent-hashing') {
          setTopicContent({ component: 'ConsistentHashing' });
        } else if (topicId === 'replication') {
          setTopicContent({ component: 'Replication' });
        } else if (topicId === 'leader-follower-architecture') {
          setTopicContent({ component: 'LeaderFollowerArchitecture' });
        } else if (topicId === 'load-balancing') {
          setTopicContent({ component: 'LoadBalancing' });
        } else if (topicId === 'caching-strategies') {
          setTopicContent({ component: 'CachingStrategies' });
        } else if (topicId === 'cdn') {
          setTopicContent({ component: 'CDN' });
        } else if (topicId === 'app-level-cache') {
          setTopicContent({ component: 'AppLevelCache' });
        } else if (topicId === 'message-queues') {
          setTopicContent({ component: 'MessageQueues' });
        } else if (topicId === 'pubsub-pattern') {
          setTopicContent({ component: 'PubSubPattern' });
        } else if (topicId === 'rate-limiting') {
          setTopicContent({ component: 'RateLimiting' });
        } else if (topicId === 'circuit-breakers') {
          setTopicContent({ component: 'CircuitBreakers' });
        } else if (topicId === 'cap-theorem') {
          setTopicContent({ component: 'CAPTheorem' });
        } else if (topicId === 'idempotent-operations') {
          setTopicContent({ component: 'IdempotentOperations' });
        } else if (topicId === 'websockets-long-polling') {
          setTopicContent({ component: 'WebSocketsLongPolling' });
        } else if (topicId === 'object-storage') {
          setTopicContent({ component: 'ObjectStorage' });
        } else if (topicId === 'bloom-filters') {
          setTopicContent({ component: 'BloomFilters' });
        } else if (topicId === 'oauth-jwt') {
          setTopicContent({ component: 'OAuthJWT' });
        } else if (topicId === 'dead-letter-queues') {
          setTopicContent({ component: 'DeadLetterQueues' });
        } else if (topicId === 'eventual-consistency') {
          setTopicContent({ component: 'EventualConsistency' });
        } else if (topicId === 'cqrs-event-sourcing') {
          setTopicContent({ component: 'CQRSEventSourcing' });
        } else if (topicId === 'metrics-monitoring') {
          setTopicContent({ component: 'MetricsMonitoring' });
        } else if (topicId === 'time-series-databases') {
          setTopicContent({ component: 'TimeSeriesDatabases' });
        } else if (topicId === 'single-responsibility-principle') {
          setTopicContent({ component: 'SingleResponsibilityPrinciple' });
        } else if (topicId === 'open-closed-principle') {
          setTopicContent({ component: 'OpenClosedPrinciple' });
        } else if (topicId === 'liskov-substitution-principle') {
          setTopicContent({ component: 'LiskovSubstitutionPrinciple' });
        } else if (topicId === 'interface-segregation-principle') {
          setTopicContent({ component: 'InterfaceSegregationPrinciple' });
        } else if (topicId === 'dependency-inversion-principle') {
          setTopicContent({ component: 'DependencyInversionPrinciple' });
        } else if (topicId === 'singleton-pattern') {
          setTopicContent({ component: 'SingletonPattern' });
        } else if (topicId === 'factory-method-pattern') {
          setTopicContent({ component: 'FactoryMethodPattern' });
        } else if (topicId === 'abstract-factory-pattern') {
          setTopicContent({ component: 'AbstractFactoryPattern' });
        } else if (topicId === 'builder-pattern') {
          setTopicContent({ component: 'BuilderPattern' });
        } else if (topicId === 'prototype-pattern') {
          setTopicContent({ component: 'PrototypePattern' });
        } else if (topicId === 'adapter-pattern') {
          setTopicContent({ component: 'AdapterPattern' });
        } else if (topicId === 'decorator-pattern') {
          setTopicContent({ component: 'DecoratorPattern' });
        } else if (topicId === 'observer-pattern') {
          setTopicContent({ component: 'ObserverPattern' });
    } else if (topicId === 'strategy-pattern') {
      setTopicContent({ component: 'StrategyPattern' });
    } else if (topicId === 'command-pattern') {
      setTopicContent({ component: 'CommandPattern' });
    } else if (topicId === 'template-method-pattern') {
      setTopicContent({ component: 'TemplateMethodPattern' });
    } else if (topicId === 'state-pattern') {
      setTopicContent({ component: 'StatePattern' });
    } else if (topicId === 'chain-of-responsibility-pattern') {
      setTopicContent({ component: 'ChainOfResponsibilityPattern' });
    } else if (topicId === 'iterator-pattern') {
      setTopicContent({ component: 'IteratorPattern' });
    } else if (topicId === 'mediator-pattern') {
      setTopicContent({ component: 'MediatorPattern' });
    } else if (topicId === 'memento-pattern') {
      setTopicContent({ component: 'MementoPattern' });
    } else if (topicId === 'visitor-pattern') {
      setTopicContent({ component: 'VisitorPattern' });
    } else if (topicId === 'interpreter-pattern') {
      setTopicContent({ component: 'InterpreterPattern' });
    } else if (topicId === 'bridge-pattern') {
      setTopicContent({ component: 'BridgePattern' });
    } else if (topicId === 'flyweight-pattern') {
      setTopicContent({ component: 'FlyweightPattern' });
    } else if (topicId === 'saga-pattern') {
      setTopicContent({ component: 'SagaPattern' });
    } else if (topicId === 'mvcc-concurrency-control') {
      setTopicContent({ component: 'MVCCConcurrencyControl' });
    } else if (topicId === 'acid-properties-isolation') {
      setTopicContent({ component: 'ACIDPropertiesIsolation' });
    } else if (topicId === 'database-keys') {
      setTopicContent({ component: 'DatabaseKeys' });
    } else if (topicId === 'sql-vs-nosql') {
      setTopicContent({ component: 'SQLvsNoSQL' });
    } else if (topicId === 'containers-vs-vms') {
      setTopicContent({ component: 'ContainersVsVMs' });
    } else if (topicId === 'kubernetes-guide') {
      setTopicContent({ component: 'KubernetesGuide' });
    } else {
      setTopicContent(getMockContent(topicId));
    }
    
    setIsLoadingContent(false);
  }, []);

  // Handle document selection - navigate to deep dive URL
  const handleDocumentSelect = (doc, company) => {
    setSelectedDoc(doc);
    setSelectedCompany(company);
    
    // Navigate to the deep dive post URL for SEO-friendly URLs
    const deepDiveUrl = DEEP_DIVE_URL_MAPPING[doc.id];
    if (deepDiveUrl) {
      navigate(deepDiveUrl);
    } else {
      // Fallback to loading content in DeepDives (for non-deep dive content)
      loadTopicContent(doc.id);
    }
  };

  // Auto-select document based on URL slug
  useEffect(() => {
    if (slug) {
      const docId = SLUG_TO_DOC_MAPPING[slug];
      if (docId) {
        // Find the document and company
        for (const company of MOCK_COMPANIES) {
          const doc = company.docs.find(d => d.id === docId);
          if (doc) {
            setSelectedDoc(doc);
            setSelectedCompany(company);
            loadTopicContent(docId);
            break;
          }
        }
      }
    }
  }, [slug, loadTopicContent]);

  // Show all companies since search is removed
  const filteredCompanies = MOCK_COMPANIES;

  // Load content when document is selected
  useEffect(() => {
    if (selectedDoc) {
      loadTopicContent(selectedDoc.id);
      // Removed loadChatHistory call since chat functionality is removed
    }
  }, [selectedDoc]);

  // Global mouse event listeners for resizing - left sidebar only
  useEffect(() => {
    const handleMouseMove = (e) => {
      handleLeftSidebarMouseMove(e);
      // Removed handleRightSidebarMouseMove since right sidebar is removed
    };

    const handleMouseUp = () => {
      handleLeftSidebarMouseUp();
      // Removed handleRightSidebarMouseUp since right sidebar is removed
    };

    if (isResizingLeft) { // Removed isResizingRight check
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingLeft]); // Removed isResizingRight dependency

  // Removed loadChatHistory function since chat functionality is removed
  // Removed handleSendMessage function since chat functionality is removed
  // Removed handleKeyPress function since chat functionality is removed

  const getMockContent = (topicId) => {
    // Simple coming soon content for all topics
    const comingSoonContent = {
      title: 'Content Coming Soon',
      content: [
        {
          type: 'Heading1',
          children: 'Content Coming Soon'
        },
        {
          type: 'ImportantNote',
          props: { type: 'info', title: 'Under Development' },
          children: 'We are currently working on creating comprehensive system design content. This section will be available soon with detailed explanations, examples, and best practices.'
        },
        {
          type: 'Paragraph',
          children: 'Stay tuned for high-quality system design documentation covering real-world scenarios and industry best practices.'
        }
      ]
    };

    return comingSoonContent;
  };

  return (
    <div className="deepdives-container">
      <SEOHead 
        title="Technical Deep Dives - Advanced CS Concepts & Interview Topics | SlazySloth"
        description="Explore advanced computer science concepts with in-depth technical articles. Master complex topics like distributed systems, machine learning, databases, and more for senior engineering interviews."
        keywords="technical deep dives, advanced computer science, distributed systems, machine learning, database internals, system internals, advanced algorithms, computer science concepts, senior engineer interview, technical articles"
        url="https://slazysloth.com/deep-dives"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Technical Deep Dives",
          "description": "Advanced technical content and in-depth exploration of computer science concepts",
          "provider": {
            "@type": "Organization",
            "name": "SlazySloth"
          },
          "educationalLevel": "Advanced",
          "teaches": ["Advanced Computer Science", "Distributed Systems", "Machine Learning", "Database Internals", "System Design"]
        }}
      />
      
      {/* Left Sidebar Toggle Button - Intersection */}
      <Box
        sx={{
          position: 'absolute',
          left: leftSidebarVisible ? leftSidebarWidth - 13 : 0,
          top: '6%',
          zIndex: 1001,
          transition: 'left 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            color: '#008080',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
              backgroundColor: '#008080',
              color: 'white',
              borderColor: '#008080',
              transform: 'scale(1.1)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
            }
          }}
          onClick={toggleLeftSidebar}
        >
          {leftSidebarVisible ? <ChevronLeft fontSize="inherit" /> : <ChevronRight fontSize="inherit" />}
        </Box>
      </Box>

      {/* Left Sidebar - Topics */}
      {leftSidebarVisible && (
        <div className="deepdives-sidebar" style={{ width: leftSidebarWidth, position: 'relative' }}>
        <div className="deepdives-sidebar-header">
          <Typography className="deepdives-sidebar-subtitle">
            Company System Designs
          </Typography>
        </div>
        
        <div className="deepdives-companies-list">
          {filteredCompanies.length > 0 ? (
            filteredCompanies.map((company) => (
              <div key={company.id} className="deepdives-company-folder">
                {/* Company Header */}
                <div 
                  className="deepdives-company-header"
                  onClick={() => toggleCompanyExpansion(company.id)}
                >
                  <span className="deepdives-company-name">
                    {expandedCompanies.has(company.id) ? '−' : '+'} {company.name}
                  </span>
                  <span className="deepdives-company-count">{company.docs.length}</span>
                </div>
                
                {/* Documents List (shown when expanded) */}
                {expandedCompanies.has(company.id) && (
                  <div className="deepdives-docs-list">
                    {company.docs.map((doc) => (
                      <div
                        key={doc.id}
                        className={`deepdives-doc-item ${selectedDoc?.id === doc.id ? 'active' : ''}`}
                        onClick={() => handleDocumentSelect(doc, company)}
                        title={doc.title} // Tooltip for full title
                      >
                        <div className="deepdives-doc-content">
                          <div className="deepdives-doc-title">{doc.title}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666', 
              fontSize: '12px',
              lineHeight: 1.4
            }}>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                Content Coming Soon
              </div>
              <div>
                We're preparing comprehensive system design case studies and documentation.
              </div>
            </div>
          )}
        </div>
        
        {/* Left Sidebar Resize Handle */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '4px',
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#008080',
              opacity: 0.3
            },
            '&:active': {
              backgroundColor: '#008080',
              opacity: 0.5
            }
          }}
          onMouseDown={handleLeftSidebarMouseDown}
        />
        </div>
      )}

      {/* Center Content Panel */}
      <div className="deepdives-content">
        {selectedDoc ? (
          <>
            <div className="deepdives-content-header">
              <Typography className="deepdives-content-title">
                {topicContent?.title || selectedDoc.title}
              </Typography>
            </div>
            
            <div className="deepdives-content-body">
              {isLoadingContent ? (
                <div className="deepdives-loading">
                  <CircularProgress size={40} sx={{ color: '#008080' }} />
                </div>
              ) : (
                <div className="deepdives-doc">
                  {topicContent?.component === 'SystemDesignGuide' ? (
                    <SystemDesignGuide />
                  ) : topicContent?.component === 'ShardingPartitioning' ? (
                    <ShardingPartitioning />
                  ) : topicContent?.component === 'ConsistentHashing' ? (
                    <ConsistentHashing />
                  ) : topicContent?.component === 'Replication' ? (
                    <Replication />
                  ) : topicContent?.component === 'LeaderFollowerArchitecture' ? (
                    <LeaderFollowerArchitecture />
                  ) : topicContent?.component === 'LoadBalancing' ? (
                    <LoadBalancing />
                  ) : topicContent?.component === 'CachingStrategies' ? (
                    <CachingStrategies />
                  ) : topicContent?.component === 'CDN' ? (
                    <CDN />
                  ) : topicContent?.component === 'AppLevelCache' ? (
                    <AppLevelCache />
                  ) : topicContent?.component === 'MessageQueues' ? (
                    <MessageQueues />
                  ) : topicContent?.component === 'PubSubPattern' ? (
                    <PubSubPattern />
                  ) : topicContent?.component === 'RateLimiting' ? (
                    <RateLimiting />
                  ) : topicContent?.component === 'CircuitBreakers' ? (
                    <CircuitBreakers />
                  ) : topicContent?.component === 'CAPTheorem' ? (
                    <CAPTheorem />
                  ) : topicContent?.component === 'IdempotentOperations' ? (
                    <IdempotentOperations />
                  ) : topicContent?.component === 'WebSocketsLongPolling' ? (
                    <WebSocketsLongPolling />
                  ) : topicContent?.component === 'ObjectStorage' ? (
                    <ObjectStorage />
                  ) : topicContent?.component === 'BloomFilters' ? (
                    <BloomFilters />
                  ) : topicContent?.component === 'OAuthJWT' ? (
                    <OAuthJWT />
                  ) : topicContent?.component === 'DeadLetterQueues' ? (
                    <DeadLetterQueues />
                  ) : topicContent?.component === 'EventualConsistency' ? (
                    <EventualConsistency />
                  ) : topicContent?.component === 'CQRSEventSourcing' ? (
                    <CQRSEventSourcing />
                  ) : topicContent?.component === 'MetricsMonitoring' ? (
                    <MetricsMonitoring />
                  ) : topicContent?.component === 'TimeSeriesDatabases' ? (
                    <TimeSeriesDatabases />
                  ) : topicContent?.component === 'SingleResponsibilityPrinciple' ? (
                    <SingleResponsibilityPrinciple />
                  ) : topicContent?.component === 'OpenClosedPrinciple' ? (
                    <OpenClosedPrinciple />
                  ) : topicContent?.component === 'LiskovSubstitutionPrinciple' ? (
                    <LiskovSubstitutionPrinciple />
                  ) : topicContent?.component === 'InterfaceSegregationPrinciple' ? (
                    <InterfaceSegregationPrinciple />
                  ) : topicContent?.component === 'DependencyInversionPrinciple' ? (
                    <DependencyInversionPrinciple />
                  ) : topicContent?.component === 'SingletonPattern' ? (
                    <SingletonPattern />
                  ) : topicContent?.component === 'FactoryMethodPattern' ? (
                    <FactoryMethodPattern />
                  ) : topicContent?.component === 'AbstractFactoryPattern' ? (
                    <AbstractFactoryPattern />
                  ) : topicContent?.component === 'BuilderPattern' ? (
                    <BuilderPattern />
                  ) : topicContent?.component === 'PrototypePattern' ? (
                    <PrototypePattern />
                  ) : topicContent?.component === 'AdapterPattern' ? (
                    <AdapterPattern />
                  ) : topicContent?.component === 'DecoratorPattern' ? (
                    <DecoratorPattern />
                  ) : topicContent?.component === 'FacadePattern' ? (
                    <FacadePattern />
                  ) : topicContent?.component === 'CompositePattern' ? (
                    <CompositePattern />
                  ) : topicContent?.component === 'ProxyPattern' ? (
                    <ProxyPattern />
                  ) : topicContent?.component === 'ObserverPattern' ? (
                    <ObserverPattern />
                  ) : topicContent?.component === 'StrategyPattern' ? (
                    <StrategyPattern />
                  ) : topicContent?.component === 'CommandPattern' ? (
                    <CommandPattern />
                  ) : topicContent?.component === 'TemplateMethodPattern' ? (
                    <TemplateMethodPattern />
                  ) : topicContent?.component === 'StatePattern' ? (
                    <StatePattern />
                  ) : topicContent?.component === 'ChainOfResponsibilityPattern' ? (
                    <ChainOfResponsibilityPattern />
                  ) : topicContent?.component === 'IteratorPattern' ? (
                    <IteratorPattern />
                  ) : topicContent?.component === 'MediatorPattern' ? (
                    <MediatorPattern />
                  ) : topicContent?.component === 'MementoPattern' ? (
                    <MementoPattern />
                  ) : topicContent?.component === 'VisitorPattern' ? (
                    <VisitorPattern />
                  ) : topicContent?.component === 'InterpreterPattern' ? (
                    <InterpreterPattern />
                  ) : topicContent?.component === 'BridgePattern' ? (
                    <BridgePattern />
                  ) : topicContent?.component === 'FlyweightPattern' ? (
                    <FlyweightPattern />
                  ) : topicContent?.component === 'SagaPattern' ? (
                    <SagaPattern />
                  ) : topicContent?.component === 'MVCCConcurrencyControl' ? (
                    <MVCCConcurrencyControl />
                  ) : topicContent?.component === 'ACIDPropertiesIsolation' ? (
                    <ACIDPropertiesIsolation />
                  ) : topicContent?.component === 'DatabaseKeys' ? (
                    <DatabaseKeys />
                  ) : topicContent?.component === 'SQLvsNoSQL' ? (
                    <SQLvsNoSQL />
                  ) : topicContent?.component === 'ContainersVsVMs' ? (
                    <ContainersVsVMs />
                  ) : topicContent?.component === 'KubernetesGuide' ? (
                    <KubernetesGuide />
                  ) : topicContent?.content ? (
                    <div>{topicContent.content}</div>
                  ) : (
                    'Content not available'
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="deepdives-empty-state">
            <DocumentIcon className="deepdives-empty-icon" />
            <Typography variant="h6" gutterBottom>
              Select a Document to Begin
            </Typography>
            <Typography variant="body2">
              Choose a company system design document from the sidebar to start learning
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepDives;
